import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Parse a plain "YYYY-MM-DD" string as a local date (avoids UTC day-shift).
function parseISODate(value) {
  const m = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? undefined : d;
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toDisplay(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}-${m}-${date.getFullYear()}`;
}

// Calendar-driven replacement for <input type="date">.
// value/onChange use plain "YYYY-MM-DD" strings, same contract as the native input.
export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = "Select date",
  disabled,
  className,
  style,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseISODate(value);
  const minDate = parseISODate(min);
  const maxDate = parseISODate(max);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          style={style}
          className={cn(
            "df-input-rect flex items-center justify-between gap-2 text-left",
            !selected && "text-white/40",
            className
          )}
        >
          <span>{selected ? toDisplay(selected) : placeholder}</span>
          <CalendarIcon size={16} className="opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => {
            if (!date) return;
            onChange(toISODate(date));
            setOpen(false);
          }}
          disabled={(date) => (minDate && date < minDate) || (maxDate && date > maxDate)}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
